import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TransactionOverlay from '../components/transactions/TransactionOverlay';
import SalesManage, { type SalesManageSaveReason } from './SalesManage';
import type { Invoice } from '../types/invoice';
import { useReceivePaymentsHub } from './ReceivePaymentsHubContext';

type InvoiceOverlayLocationState = {
  returnTo?: string;
};

const ReceivePaymentsInvoiceOverlay = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchInvoices } = useReceivePaymentsHub();
  const routeState = location.state as InvoiceOverlayLocationState | null;

  const parsedId = id != null ? Number(id) : Number.NaN;
  const invoiceId = Number.isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;

  const close = useCallback(() => {
    const returnTo = routeState?.returnTo;
    if (returnTo) {
      navigate(returnTo, { preventScrollReset: true });
      return;
    }
    navigate('/receive-payments', { preventScrollReset: true });
  }, [navigate, routeState?.returnTo]);

  const handleInvoiceSaved = useCallback(
    (invoice: Invoice, reason: SalesManageSaveReason) => {
      refetchInvoices();

      if (reason === 'draft_created') {
        navigate(`invoice/${invoice.id}`, {
          replace: true,
          preventScrollReset: true,
          state: routeState,
        });
        return;
      }

      if (reason === 'posted' || reason === 'amended' || reason === 'draft_updated') {
        close();
      }
    },
    [close, navigate, refetchInvoices, routeState]
  );

  return (
    <TransactionOverlay headerless variant="wide" onClose={close}>
      <SalesManage
        key={invoiceId ?? 'invalid'}
        embedded
        invoiceId={invoiceId}
        onClose={close}
        onInvoiceSaved={handleInvoiceSaved}
      />
    </TransactionOverlay>
  );
};

export default ReceivePaymentsInvoiceOverlay;
